// Sidebar list of the signed-in user's own conversations (07-chat.md §2.8).
// Used by both the app-shell slide-out panel and the full /chat page.

import { useQuery } from '@tanstack/react-query'
import type { ChatConversation } from '@goblic/shared'
import { chatApi } from '../../lib/api-client'
import { useLookups } from '../../hooks/useLookups'
import { EmptyState, ErrorNote, LoadingRows } from '../ui'

export function ConversationList({
  activeId,
  onSelect,
  onNew,
}: {
  activeId: string | undefined
  onSelect: (conversation: ChatConversation) => void
  onNew: () => void
}) {
  const lookups = useLookups()
  const conversationsQuery = useQuery({
    queryKey: ['chat-conversations'],
    queryFn: () => chatApi.listConversations(),
    refetchInterval: 15_000,
  })

  const items = conversationsQuery.data?.items ?? []

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-line px-3 py-2.5">
        <p className="text-sm font-medium text-ink-2">Conversations</p>
        <button type="button" className="btn-primary btn-sm" onClick={onNew}>
          New
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {conversationsQuery.isLoading ? (
          <LoadingRows label="Loading conversations…" />
        ) : conversationsQuery.isError ? (
          <ErrorNote error={conversationsQuery.error} what="conversations" />
        ) : items.length === 0 ? (
          <EmptyState title="No conversations yet" art={null}>
            Start one to ask about your projects, workflows or agents.
          </EmptyState>
        ) : (
          <ul>
            {items.map((conversation) => (
              <li key={conversation.id}>
                <button
                  type="button"
                  onClick={() => onSelect(conversation)}
                  aria-current={conversation.id === activeId ? 'true' : undefined}
                  className={`block w-full truncate border-b border-line px-3.5 py-2.5 text-left text-base transition-colors hover:bg-sunken ${
                    conversation.id === activeId
                      ? 'bg-moss-soft text-moss-strong'
                      : 'text-ink'
                  }`}
                >
                  <span className="block truncate font-medium">
                    {conversation.title || 'Untitled conversation'}
                  </span>
                  <span className="block truncate text-sm text-ink-3">
                    {conversation.status === 'running'
                      ? 'Running…'
                      : (lookups.connections.find((c) => c.id === conversation.credentialId)?.name ?? 'No connection')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
